import { Color, Web3DViewer } from '@technology/web3d';
import { IMetaMaterial, TrimbimModel } from '@technology/web3d-plugin-trimbim';
import { Accessor, createSignal } from 'solid-js';
import { SingleCapabilityBase } from './base/single-capability-base';
import { CapabilityCategory } from './base/models/capability-category';
import { getLogger } from '@trimble/logging';
import { RGB_MAX_VALUE, type RGBFormat } from '@trimble/utilities';

/**
 * Enables actions on selected objects and entities such as material styling,
 * visibility toggling, layer isolation, and fit-to-view.
 */
export class SelectionActionsCapability extends SingleCapabilityBase {
  public override readonly category: CapabilityCategory =
    CapabilityCategory.Command;

  public readonly color: Accessor<RGBFormat | undefined>;
  public readonly opacity: Accessor<number | undefined>;
  public readonly initialOpacity: Accessor<number>;
  public readonly isHideSelectionActive: Accessor<boolean>;
  public readonly isShowOnlySelectedActive: Accessor<boolean>;
  public readonly selection: Accessor<Map<string, number[]>>;

  private readonly _setColor: (v: RGBFormat | undefined) => void;
  private readonly _setOpacity: (v: number | undefined) => void;
  private readonly _setInitialOpacity: (v: number) => void;
  private readonly _setIsHideSelectionActive: (v: boolean) => void;
  private readonly _setIsShowOnlySelectedActive: (v: boolean) => void;
  private readonly _setSelection: (v: Map<string, number[]>) => void;

  private _computeInitialOpacityAbortController: AbortController | undefined;

  protected logger = getLogger();

  constructor(viewer: Web3DViewer) {
    super(viewer);

    const [color, setColor] = createSignal<RGBFormat | undefined>(undefined);
    this.color = color;
    this._setColor = setColor;

    const [opacity, setOpacity] = createSignal<number | undefined>(undefined);
    this.opacity = opacity;
    this._setOpacity = setOpacity;

    const [initialOpacity, setInitialOpacity] = createSignal<number>(1);
    this.initialOpacity = initialOpacity;
    this._setInitialOpacity = setInitialOpacity;

    const [isHideSelectionActive, setIsHideSelectionActive] =
      createSignal(false);
    this.isHideSelectionActive = isHideSelectionActive;
    this._setIsHideSelectionActive = setIsHideSelectionActive;

    const [isShowOnlySelectedActive, setIsShowOnlySelectedActive] =
      createSignal(false);
    this.isShowOnlySelectedActive = isShowOnlySelectedActive;
    this._setIsShowOnlySelectedActive = setIsShowOnlySelectedActive;

    const [selection, setSelection] = createSignal<Map<string, number[]>>(
      new Map()
    );
    this.selection = selection;
    this._setSelection = setSelection;
  }

  protected override trackIsEnabled(): void {
    this.enable();
  }

  public override async cleanup(): Promise<void> {
    this._computeInitialOpacityAbortController?.abort();
  }

  public setSelection(selection: Map<string, number[]>): void {
    this._setSelection(selection);
    this._setColor(undefined);
    this._setOpacity(undefined);
    this._setIsHideSelectionActive(false);
    this._setIsShowOnlySelectedActive(false);

    this._computeInitialOpacityAbortController?.abort();
    const controller = new AbortController();
    this._computeInitialOpacityAbortController = controller;

    void this.computeInitialOpacity(selection, controller.signal)
      .then((val) => {
        if (controller.signal.aborted) return;
        this._setInitialOpacity(val);
      })
      .catch((err: unknown) => {
        if (err instanceof DOMException && err.name === 'AbortError') return;
        this.logger.error('Failed to compute initial opacity', err);
      });
  }

  public setColor(color: RGBFormat | undefined): void {
    this._setColor(color);
    this.applyColor(color);
  }

  public setOpacity(value: number | undefined): void {
    this._setOpacity(value);
    this.applyOpacity(value);
  }

  public async fitToView(): Promise<void> {
    const sel = this.selection();
    if (sel.size === 0) return;
    await this.viewer.camera.fitToView({ models: sel });
  }

  public async resetMaterials(): Promise<void> {
    const sel = this.selection();
    if (sel.size === 0) return;

    this._setColor(undefined);
    this._setOpacity(undefined);
    this._setInitialOpacity(1);

    await Promise.all(
      Array.from(sel.entries()).map(async ([modelId, entityIds]) => {
        const model = this.viewer.getModel(modelId);
        if (!(model instanceof TrimbimModel)) return;
        await model.resetCustomMaterials(entityIds);
      })
    );
  }

  public async hideSelection(): Promise<void> {
    const sel = this.selection();
    if (sel.size === 0) return;

    const newState = !this.isHideSelectionActive();
    this._setIsHideSelectionActive(newState);
    if (newState) {
      this._setIsShowOnlySelectedActive(false);
    }

    await Promise.all(
      Array.from(sel.entries()).map(async ([modelId, entityIds]) => {
        const model = this.viewer.getModel(modelId);
        if (!(model instanceof TrimbimModel)) return;
        await model.setVisibility(entityIds, !newState);
      })
    );
  }

  public async showOnlySelected(): Promise<void> {
    const sel = this.selection();
    if (sel.size === 0) return;

    const showOnly = !this.isShowOnlySelectedActive();
    this._setIsShowOnlySelectedActive(showOnly);
    if (showOnly) {
      this._setIsHideSelectionActive(false);
    }

    await Promise.all(
      this.viewer.getModels(TrimbimModel).map(async (model) => {
        await model.setVisibility(undefined, !showOnly);
      })
    );

    if (!showOnly) return;

    await Promise.all(
      Array.from(sel.entries()).map(async ([modelId, entityIds]) => {
        const model = this.viewer.getModel(modelId);
        if (!(model instanceof TrimbimModel)) return;

        const [parents, children] = await Promise.all([
          model.getHierarchyParents(entityIds, undefined, true),
          model.getHierarchyChildren(entityIds, undefined, true),
        ]);

        const uniqueIds = [...new Set([...entityIds, ...parents, ...children])];
        await model.setVisibility(uniqueIds, true);
      })
    );
  }

  public async showAll(): Promise<void> {
    this._setIsHideSelectionActive(false);
    this._setIsShowOnlySelectedActive(false);

    await Promise.all(
      this.viewer.getModels(TrimbimModel).map(async (model) => {
        await model.setVisibility(undefined, true);
      })
    );
  }

  public async isolateLayers(): Promise<void> {
    const sel = this.selection();
    if (sel.size === 0) return;

    await Promise.all(
      this.viewer.getModels(TrimbimModel).map(async (model) => {
        const allLayers = await model.getLayers();
        const allLayerIds = allLayers.map((_, index) => index);
        if (allLayerIds.length > 0) {
          await model.setLayerVisibilities(allLayerIds, false);
        }

        if (sel.has(model.modelId)) {
          const entityIds = sel.get(model.modelId) ?? [];
          const selectedLayerIds = await model.findEntityLayers(entityIds);
          const uniqueLayerIds = [...new Set(selectedLayerIds)];
          if (uniqueLayerIds.length > 0) {
            await model.setLayerVisibilities(uniqueLayerIds, true);
          }
        }
      })
    );
  }

  public async unisolateLayers(): Promise<void> {
    await Promise.all(
      this.viewer.getModels(TrimbimModel).map(async (model) => {
        const allLayers = await model.getLayers();
        const allLayerIds = allLayers.map((_, index) => index);

        if (allLayerIds.length > 0) {
          await model.setLayerVisibilities(allLayerIds, true);
        }
      })
    );
  }

  private applyColor(newColor: RGBFormat | undefined): void {
    const sel = this.selection();
    if (sel.size === 0 || newColor === undefined) return;

    const material: IMetaMaterial = {
      color: new Color(
        newColor.r / RGB_MAX_VALUE,
        newColor.g / RGB_MAX_VALUE,
        newColor.b / RGB_MAX_VALUE
      ),
    };

    for (const [modelId, entityIds] of sel) {
      const model = this.viewer.getModel(modelId);
      if (!(model instanceof TrimbimModel)) continue;
      void model.setCustomMaterial(entityIds, material);
    }
  }

  private applyOpacity(newOpacity: number | undefined): void {
    const sel = this.selection();
    if (sel.size === 0 || newOpacity === undefined) return;

    const material: IMetaMaterial = {
      opacity: newOpacity,
    };

    for (const [modelId, entityIds] of sel) {
      const model = this.viewer.getModel(modelId);
      if (!(model instanceof TrimbimModel)) continue;
      void model.setCustomMaterial(entityIds, material);
    }
  }

  private async computeInitialOpacity(
    selection: Map<string, number[]>,
    signal: AbortSignal
  ): Promise<number> {
    const materials = await Promise.all(
      Array.from(selection.entries()).map(async ([modelId, entityIds]) => {
        const model = this.viewer.getModel(modelId);
        if (!(model instanceof TrimbimModel)) return undefined;
        const result = await model.getCustomMaterials(entityIds);
        signal.throwIfAborted();
        return result;
      })
    );

    signal.throwIfAborted();

    const nonTrivial = materials
      .flatMap((m) => m)
      .filter((m): m is IMetaMaterial => {
        return m !== undefined && m.opacity !== undefined && m.opacity !== 1;
      });

    const uniqueOpacities = new Set(nonTrivial.map((m) => m.opacity));
    if (uniqueOpacities.size > 1) return 0;

    return uniqueOpacities.values().next().value ?? 1;
  }
}
